import {
    BreakerStepSummary,
    ConditionStepSummary,
    DelayStepSummary,
    EndStepSummary,
    HttpStepSummary,
    IntegrationStepSummary,
    ModifyStepSummary,
    SetStepSummary,
    StepSummary,
    WebhookStepSummary,
} from "./types";

import StepRunData = Paldock.LaravelConnectionCreator.Data.StepRunData;

export interface StepLogContext {
    /** http_status_code from IntegrationLogData — lives on the log, not on StepRunData */
    httpStatusCode?: number | null;
    /** message from IntegrationLogData — used by set/modify to carry key=>value / before=>after data */
    message?: string | null;
    /** conversionState from IntegrationLogData — formSubmissionValues used as lead data for integration steps */
    conversionState?: App.Data.ConversionStateData | null;
}

/**
 * Extracts a typed summary from a stepRun.
 * Used in table cells (compact view) and as typed input for StepDetail* components.
 * Returns null if the step type is unsupported or data is missing.
 *
 * @param logContext - Extra fields from the parent log record (IntegrationLogData) that are
 *                    not available on StepRunData itself (e.g. http_status_code, message).
 */
export function extractStepSummary(stepRun: StepRunData, logContext?: StepLogContext): StepSummary | null {
    try {
        const { type, data_out, step_id, id } = stepRun;

        if (type === "http") {
            // BE provides data directly in data_out (not nested under step_id)
            // method comes from stepRun.settings['method']
            const method = (stepRun.settings?.["method"] as string | null) ?? null;
            return {
                type,
                id,
                response: {
                    // http_status_code lives on IntegrationLogData, passed via logContext
                    status: logContext?.httpStatusCode ?? null,
                    uri: (data_out?.["uri"] as string | null) ?? null,
                    method,
                    headers: (data_out?.["headers"] as Record<string, string[]> | null) ?? null,
                    body: (data_out?.["body"] as unknown) ?? null,
                },
                request: {
                    uri: (data_out?.["uri"] as string | null) ?? null,
                    method,
                    headers: (data_out?.["request_headers"] as Record<string, string> | null) ?? null,
                    body: (data_out?.["request_body"] as unknown) ?? null,
                    query: (data_out?.["query"] as Record<string, string> | null) ?? null,
                },
            } satisfies HttpStepSummary;
        }

        if (type === "webhook") {
            let parsed: Record<string, unknown> = {};
            try {
                if (logContext?.message) {
                    const parsedMessage = JSON.parse(logContext.message) as unknown;
                    if (parsedMessage && typeof parsedMessage === "object" && !Array.isArray(parsedMessage)) {
                        parsed = parsedMessage as Record<string, unknown>;
                    }
                }
            } catch {
                // leave parsed empty
            }
            return {
                type,
                id,
                request: {
                    method: (parsed["method"] as string | null) ?? null,
                    query: (parsed["query"] as Record<string, string> | null) ?? null,
                    payload: (parsed["payload"] as unknown[] | null) ?? null,
                },
            } satisfies WebhookStepSummary;
        }

        if (type === "set") {
            if (logContext?.message) {
                // message format: "key => value\nkey => value\n"
                const values = logContext.message
                    .split("\n")
                    .filter(Boolean)
                    .map((line) => {
                        const separatorIndex = line.indexOf(" => ");
                        const key = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line;
                        const value = separatorIndex >= 0 ? line.slice(separatorIndex + 4) : "";
                        return { key, value };
                    });
                return { type: "set", id, values } satisfies SetStepSummary;
            }
            // fallback to old path if no message available
            const stepEntry = (step_id == null ? undefined : data_out?.[step_id]) as
                | { _step?: { settings?: { values?: { key: string; value: string }[] } } }
                | undefined;
            const values = stepEntry?._step?.settings?.values ?? [];
            return { type: "set", id, values } satisfies SetStepSummary;
        }

        if (type === "modify") {
            if (logContext?.message) {
                let parsed: Record<string, unknown> = {};
                try {
                    parsed = JSON.parse(logContext.message) as Record<string, unknown>;
                } catch {
                    // leave parsed empty so the detail degrades to an empty changes list
                }
                const changes = Object.values(parsed).map((entry) => {
                    const e = entry as { field: string; before: unknown; after: unknown; modifications?: unknown[] };
                    return {
                        field: e.field,
                        before: e.before,
                        after: e.after,
                        modifications: (e.modifications ?? []).map((m) => {
                            const mod = m as { modificator: string; params: unknown; before: unknown; after: unknown };
                            return {
                                modificator: mod.modificator,
                                params: mod.params,
                                before: mod.before,
                                after: mod.after,
                            };
                        }),
                    };
                });
                return { type: "modify", id, changes } satisfies ModifyStepSummary;
            }
            return { type: "modify", id, changes: [] } satisfies ModifyStepSummary;
        }

        if (type === "breaker") {
            // repeats comes from stepRun.settings['repeats']
            return {
                type: "breaker",
                id,
                repeats: (stepRun.settings?.["repeats"] as string | null) ?? null,
            } satisfies BreakerStepSummary;
        }

        if (type === "delay") {
            // delay_seconds comes from stepRun.settings['delay_seconds']
            return {
                type: "delay",
                id,
                delaySeconds: (stepRun.settings?.["delay_seconds"] as number | null) ?? null,
            } satisfies DelayStepSummary;
        }

        if (type === "integration") {
            const stepEntry = (step_id == null ? undefined : data_out?.[step_id]) as
                | { _step?: { settings?: { meaning?: IntegrationStepSummary["meaning"] } } }
                | undefined;
            const leadData = (logContext?.conversionState?.formSubmissionValues ?? []).map(({ name, value }) => ({
                key: name,
                value: value ?? "",
            }));
            return {
                type: "integration",
                id,
                meaning: stepEntry?._step?.settings?.meaning ?? null,
                leadData,
            } satisfies IntegrationStepSummary;
        }

        if (type === "condition") {
            const source =
                Array.isArray(stepRun.settings) && stepRun.settings.length > 0
                    ? (stepRun.settings as Array<Record<string, unknown>>)
                    : Array.isArray(data_out)
                      ? (data_out as Array<Record<string, unknown>>)
                      : [];
            const normalizeState = (value: unknown): boolean | null => {
                if (typeof value === "boolean") return value;
                if (value === 1 || value === "1" || value === "true") return true;
                if (value === 0 || value === "0" || value === "false") return false;
                return null;
            };
            return {
                type: "condition",
                id,
                rules: source.map((rule) => ({
                    key: rule.key != null ? String(rule.key) : "-",
                    operator: rule.operator != null ? String(rule.operator) : "-",
                    value: rule.value != null ? String(rule.value) : "-",
                    value2: rule.value2 != null ? String(rule.value2) : "-",
                    state: normalizeState(rule.state),
                })),
            } satisfies ConditionStepSummary;
        }
        if (type === "success") {
            return {
                type: "success",
                id,
                result: logContext?.message ?? null,
            } satisfies EndStepSummary;
        }
    } catch {
        return null;
    }

    return null;
}
